import {
  ExtranetActivationCodeDto,
  ExtranetCustomerUserProfessionalRoleDto,
  ExtranetProductProfessionalRoleDto,
} from '@pharmac-extranet/extranet-api'
import { useLanguage } from '@pharmac-extranet/shared'
import { PharmacH4, PharmacSuccessToast, usePharmacToast } from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthenticationContext } from '../../../features/authentication/useAuthentication'
import { api } from '../../../services/HttpService'
import { ConfirmationModal } from '../../confirmation-modal/ConfirmationModal'
import { useShop } from '../useShop'
import { ShopActivateCodeModalContent } from './ShopActivateCodeModalContent'

type Props = {
  customerId: number
  activateProductCode: boolean
  closeModal: () => void
}

export const ShopActivateCodeModal = ({ customerId, activateProductCode, closeModal }: Props) => {
  const { t, i18n } = useLanguage()
  const [codeInput, setCodeInput] = useState<string>('')
  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()
  const { user, isPharmacCustomerService } = useAuthenticationContext()
  const navigate = useNavigate()
  const { langQuery } = useShop()

  const {
    data: previewProduct,
    isLoading: previewIsLoading,
    isError: previewError,
  } = useQuery({
    queryKey: ['__getProduct', codeInput],
    queryFn: async () =>
      await api.shop.previewActivationCodeProduct(Number(customerId), codeInput, {
        language: langQuery(i18n.language),
      }),
  })

  const { mutate: subscribeWithCode } = useMutation({
    mutationFn: (data: ExtranetActivationCodeDto) =>
      api.shop.subscribeWithActivationCode(Number(customerId), data),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:shop.activateCodeModal.activateProductSuccess')}
          </PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: ['__subscribeWithActivationCode'],
      })
      closeModal
      navigate(0)
    },
  })

  const { data: productData } = useQuery({
    queryKey: ['__getProduct', previewProduct?.id],
    queryFn: async () =>
      await api.shop.getShopProductById(Number(customerId), Number(previewProduct?.id)),
  })

  const hasRequiredRoles = (
    productRoles?: ExtranetProductProfessionalRoleDto[],
    userRoles?: ExtranetCustomerUserProfessionalRoleDto[]
  ) => {
    if (productData?.professionalRoles && productData?.professionalRoles.length > 0)
      return productRoles?.some((pr) =>
        userRoles?.find((ur) => pr.displayName === ur.displayName.fi)
      )
    return true
  }

  const errorCodes = previewProduct?.localizableMessages.map((message) => message.messageKey)
  const alreadyUsedError = errorCodes?.includes(
    'error.shop.validation.activation-code-already-used'
  )
  const alreadyActivatedError = errorCodes?.includes(
    'error.shop.validation.product-already-activated'
  )
  const notActiveError = errorCodes?.includes('error.shop.validation.product-not-active')
  const activatedError = errorCodes?.includes('error.shop.validation.product-already-activated')

  return (
    <ConfirmationModal
      showOnProp={activateProductCode}
      title={<PharmacH4 color="purple">{t('extranet:shop.activateCodeModal.title')}</PharmacH4>}
      onSubmit={() => subscribeWithCode({ activationCode: codeInput })}
      onClose={closeModal}
      closeButtonText={t('common:cancel')}
      submitButtonText={t('extranet:shop.buttons.activate')}
      disableOnSubmit={
        !!!previewProduct ||
        alreadyActivatedError ||
        alreadyUsedError ||
        notActiveError ||
        isPharmacCustomerService ||
        !hasRequiredRoles(productData?.professionalRoles, user?.professionalRoles)
      }
      content={
        <ShopActivateCodeModalContent
          alreadyActivatedError={alreadyActivatedError}
          alreadyUsedError={alreadyUsedError}
          notActiveError={notActiveError}
          activatedError={activatedError}
          codeInput={codeInput}
          setCodeInput={setCodeInput}
          previewError={previewError}
          previewIsLoading={previewIsLoading}
          previewProduct={previewProduct}
          hasRoles={!!hasRequiredRoles(productData?.professionalRoles, user?.professionalRoles)}
        />
      }
      preventClose
    />
  )
}
